/**
 * Detects if a canvas has any transparent pixels
 * @param {HTMLCanvasElement} canvas - The canvas to analyze
 * @returns {boolean} - True if transparency detected, false otherwise
 */
function detectTransparency(canvas) {
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Sample pixels to check for transparency (alpha < 255)
  const step = Math.max(1, Math.floor((canvas.width * canvas.height) / 10000)); // Sample up to 10,000 pixels

  for (let i = 3; i < data.length; i += 4 * step) {
    if (data[i] < 255) {
      return true;
    }
  }
  return false;
}

/**
 * Analyzes the border pixels of an image to detect if there's a uniform color
 * @param {HTMLImageElement} img - The image element to analyze
 * @param {number} borderWidth - Width of border to sample (default: 2 pixels)
 * @param {number} tolerance - Color similarity tolerance (0-255, default: 30)
 * @returns {Object|null} - {r, g, b, a} color object if uniform border found, null otherwise
 */
export function detectUniformBorderColor(img, borderWidth = 2, tolerance = 30) {
  console.log(
    '🔍 detectUniformBorderColor - Image dimensions:',
    img.width,
    'x',
    img.height,
  );
  console.log(
    '🔍 Border width:',
    borderWidth,
    'Tolerance:',
    tolerance,
    '(side borders only, skipping outermost 1 pixel)',
  );

  // Create temporary canvas to analyze the image
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  // Get image data
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const width = canvas.width;
  const height = canvas.height;

  // Sample only side border pixels (left and right) since we only add padding to sides
  const borderPixels = [];
  const skipOuterPixels = 1; // Skip the outermost 1 pixel
  const actualBorderWidth = Math.min(
    borderWidth,
    Math.min(width, height) - skipOuterPixels,
  );

  if (actualBorderWidth <= 0) {
    console.log('❌ Image too small for border sampling');
    return null;
  }

  // Left border (skip outermost 1 pixel)
  for (let y = skipOuterPixels; y < height - skipOuterPixels; y++) {
    for (
      let x = skipOuterPixels;
      x < skipOuterPixels + actualBorderWidth;
      x++
    ) {
      const idx = (y * width + x) * 4;
      borderPixels.push({
        r: data[idx],
        g: data[idx + 1],
        b: data[idx + 2],
        a: data[idx + 3],
      });
    }
  }

  // Right border (skip outermost 1 pixel)
  for (let y = skipOuterPixels; y < height - skipOuterPixels; y++) {
    for (
      let x = Math.max(
        width - skipOuterPixels - actualBorderWidth,
        skipOuterPixels,
      );
      x < width - skipOuterPixels;
      x++
    ) {
      const idx = (y * width + x) * 4;
      borderPixels.push({
        r: data[idx],
        g: data[idx + 1],
        b: data[idx + 2],
        a: data[idx + 3],
      });
    }
  }

  if (borderPixels.length === 0) return null;

  // Calculate average color
  let totalR = 0,
    totalG = 0,
    totalB = 0,
    totalA = 0;
  for (const pixel of borderPixels) {
    totalR += pixel.r;
    totalG += pixel.g;
    totalB += pixel.b;
    totalA += pixel.a;
  }

  const avgColor = {
    r: Math.round(totalR / borderPixels.length),
    g: Math.round(totalG / borderPixels.length),
    b: Math.round(totalB / borderPixels.length),
    a: Math.round(totalA / borderPixels.length),
  };

  // Find the majority background color instead of averaging
  const colorGroups = new Map();

  // Group similar colors together
  for (const pixel of borderPixels) {
    // Create a color key by rounding to nearest 10 for grouping similar colors
    const colorKey = `${Math.round(pixel.r / 10) * 10},${
      Math.round(pixel.g / 10) * 10
    },${Math.round(pixel.b / 10) * 10},${Math.round(pixel.a / 10) * 10}`;

    if (!colorGroups.has(colorKey)) {
      colorGroups.set(colorKey, {
        count: 0,
        totalR: 0,
        totalG: 0,
        totalB: 0,
        totalA: 0,
        pixels: [],
      });
    }

    const group = colorGroups.get(colorKey);
    group.count++;
    group.totalR += pixel.r;
    group.totalG += pixel.g;
    group.totalB += pixel.b;
    group.totalA += pixel.a;
    group.pixels.push(pixel);
  }

  console.log('🔍 Total border pixels sampled:', borderPixels.length);
  console.log('🔍 Color groups found:', colorGroups.size);

  // Log all color groups for debugging
  for (const [colorKey, group] of colorGroups) {
    const percentage = Math.round((group.count / borderPixels.length) * 100);
    const avgColor = {
      r: Math.round(group.totalR / group.count),
      g: Math.round(group.totalG / group.count),
      b: Math.round(group.totalB / group.count),
      a: Math.round(group.totalA / group.count),
    };
    console.log(
      `🔍 Color group ${colorKey}: ${group.count} pixels (${percentage}%) - rgb(${avgColor.r}, ${avgColor.g}, ${avgColor.b}, ${avgColor.a})`,
    );
  }

  // Find the most common color group
  let maxCount = 0;
  let majorityColor = null;

  for (const [colorKey, group] of colorGroups) {
    if (group.count > maxCount) {
      maxCount = group.count;
      majorityColor = {
        r: Math.round(group.totalR / group.count),
        g: Math.round(group.totalG / group.count),
        b: Math.round(group.totalB / group.count),
        a: Math.round(group.totalA / group.count),
      };
    }
  }

  // Require at least 60% of border pixels to be the majority color
  const majorityThreshold = 0.6;
  const majorityPercentage = (maxCount / borderPixels.length) * 100;
  console.log(
    `🔍 Majority color: rgb(${majorityColor.r}, ${majorityColor.g}, ${
      majorityColor.b
    }, ${majorityColor.a}) - ${Math.round(
      majorityPercentage,
    )}% of border pixels`,
  );
  console.log(`🔍 Threshold: ${Math.round(majorityThreshold * 100)}%`);

  if (maxCount / borderPixels.length >= majorityThreshold) {
    console.log(
      `✅ Majority background color detected: rgb(${majorityColor.r}, ${
        majorityColor.g
      }, ${majorityColor.b}) - ${Math.round(
        majorityPercentage,
      )}% of border pixels`,
    );
    return majorityColor;
  }

  console.log(
    `❌ No clear majority background color: most common color only ${Math.round(
      majorityPercentage,
    )}% of border pixels (need ${Math.round(majorityThreshold * 100)}%)`,
  );
  return null;
}

/**
 * Creates a 16:9 canvas with smart padding for images taller than 16:9, or leaves complex images as-is
 * @param {HTMLImageElement} img - The source image
 * @param {CanvasRenderingContext2D} ctx - The destination canvas context
 * @param {HTMLCanvasElement} canvas - The destination canvas
 * @returns {void}
 */
export function processVerticalImageSmart(img, ctx, canvas) {
  console.log(
    '🔍 Processing vertical image - Dimensions:',
    img.width,
    'x',
    img.height,
  );
  console.log(
    '🔍 Aspect ratio:',
    (img.width / img.height).toFixed(2),
    '(16:9 = 1.78)',
  );

  // First check if image has transparency
  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');
  tempCanvas.width = img.width;
  tempCanvas.height = img.height;
  tempCtx.drawImage(img, 0, 0);

  const hasTransparency = detectTransparency(tempCanvas);
  console.log('🔍 Has transparency:', hasTransparency);

  if (hasTransparency) {
    // Transparent image - add transparent padding to sides to make 16:9
    const targetWidth = img.height * (16 / 9);
    canvas.width = targetWidth;
    canvas.height = img.height;

    // No need to fill background - canvas is transparent by default
    // Center the image horizontally in the 16:9 canvas
    const offsetX = (targetWidth - img.width) / 2;
    ctx.drawImage(img, offsetX, 0, img.width, img.height);

    console.log('✅ Applied transparent padding to 16:9');
  } else {
    // Check for uniform border color
    console.log('🔍 Checking for uniform border color...');
    const borderColor = detectUniformBorderColor(img);
    console.log('🔍 Detected border color:', borderColor);

    if (borderColor && borderColor.a > 200) {
      // Only use opaque borders
      // Use uniform border color for padding - make it 16:9
      const targetWidth = img.height * (16 / 9);
      canvas.width = targetWidth;
      canvas.height = img.height;

      // Fill with detected border color
      ctx.fillStyle = `rgba(${borderColor.r}, ${borderColor.g}, ${
        borderColor.b
      }, ${borderColor.a / 255})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Center the image horizontally in the 16:9 canvas
      const offsetX = (targetWidth - img.width) / 2;
      ctx.drawImage(img, offsetX, 0, img.width, img.height);

      console.log(
        '✅ Applied smart border padding to 16:9 with color:',
        `rgb(${borderColor.r}, ${borderColor.g}, ${borderColor.b})`,
      );
    } else {
      // No uniform border and not transparent - decide based on how tall the image is
      const aspectRatio = img.width / img.height;

      if (aspectRatio >= 1.0) {
        // Image is square or wider than square - leave as original
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0, img.width, img.height);

        console.log('Complex image (square or wider) - left as original');
      } else {
        // Image is taller than square - crop top/bottom to make square
        const squareSize = Math.min(img.width, img.height);
        canvas.width = squareSize;
        canvas.height = squareSize;

        // For truly vertical images, crop top/bottom to make square
        const cropX = 0; // Don't crop sides
        const cropY = (img.height - squareSize) / 2; // Crop top/bottom equally

        ctx.drawImage(
          img,
          cropX,
          cropY, // Source position
          squareSize,
          squareSize, // Source size
          0,
          0, // Destination position
          squareSize,
          squareSize, // Destination size
        );

        console.log(
          'Complex vertical image - cropped top/bottom to make square',
        );
      }
    }
  }
}
